/**
 * AskPanel — the Ask Resida Help conversation: the notice at collection, the thread, and the composer,
 * or the conversation history. The same component fills the panel's Ask tab and the full-width
 * /help/chat page; both share the one conversation held by HelpProvider.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { Button } from '../ui/button';
import { EmptyState } from '../layout/EmptyState';
import { Skeleton } from '../ui/skeleton';
import { HelpLoadError } from './ArticleList';
import { ChatComposer } from './ChatComposer';
import { ChatMessage, StreamingMessage, TypingIndicator } from './ChatMessage';
import { ConversationHistory } from './ConversationHistory';
import { useHelp } from './HelpProvider';
import type { ContactHandoffState } from './contact-handoff';

/** Notice at collection (NFR-COMPLIANCE-3, BR-28), shown before the first message and kept at the top of the thread. */
function ChatNotice() {
  return (
    <div
      className="flex gap-2.5 rounded-lg border border-info/25 bg-info/10 p-3 text-[13px]"
      data-testid="help-chat-notice"
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
      <div className="text-foreground">
        <p>
          You are chatting with an AI assistant. Answers come from Resida help articles and can be wrong. Do not enter
          sensitive personal information. Messages are processed by an AI service provider and kept 90 days.
        </p>
        <a
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="help-chat-privacy-link"
          className="inline-flex min-h-[44px] items-center font-semibold text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Privacy policy
        </a>
      </div>
    </div>
  );
}

interface AskPanelProps {
  /** In the panel, citations and "Search help" stay on the page behind it; on /help/chat they navigate. */
  variant: 'panel' | 'page';
}

export function AskPanel({ variant }: AskPanelProps) {
  const { chat, chatDisabled, historyOpen, setHistoryOpen, closePanel, setTab, openArticle } = useHelp();
  const navigate = useNavigate();
  const inPanel = variant === 'panel';
  const threadRef = useRef<HTMLDivElement>(null);
  // True while the contact-form draft is being fetched, so a slow network does not look like a dead button.
  const [escalating, setEscalating] = useState(false);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [chat.messages.length, chat.streamingText, chat.phase, historyOpen]);

  // Escalation opens the contact form prefilled with the transcript; nothing is sent (BR-27). If the draft
  // cannot be fetched the form still opens, with a note that the conversation was not attached.
  const contactSupport = useCallback(async () => {
    if (escalating) return;
    setEscalating(true);
    let state: ContactHandoffState | undefined;
    try {
      const draft = await chat.escalate();
      if (draft) state = { subject: draft.subject, body: draft.body };
    } catch {
      state = { attachFailed: true };
    }
    setEscalating(false);
    if (inPanel) closePanel();
    navigate('/support', state ? { state } : undefined);
  }, [chat, escalating, inPanel, closePanel, navigate]);

  const searchHelp = useCallback(() => {
    if (inPanel) setTab('articles');
    else navigate('/help');
  }, [inPanel, setTab, navigate]);

  if (chatDisabled) {
    return (
      <EmptyState
        title="Ask Resida Help isn't available right now"
        description="You can still search the help articles or contact support."
        data-testid="help-chat-disabled"
        action={
          <div className="flex flex-col items-stretch gap-2">
            <Button asChild>
              <Link to="/help" onClick={closePanel} data-testid="help-chat-disabled-center">
                Browse help articles
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/support" onClick={closePanel} data-testid="help-chat-disabled-contact">
                Contact support
              </Link>
            </Button>
          </div>
        }
      />
    );
  }

  if (historyOpen) {
    return (
      <ConversationHistory
        onOpen={(conversationId) => {
          void chat.loadConversation(conversationId);
          setHistoryOpen(false);
        }}
        onNew={() => {
          chat.newConversation();
          setHistoryOpen(false);
        }}
        onDeleted={(conversationId) => {
          if (conversationId === chat.conversationId) chat.newConversation();
        }}
      />
    );
  }

  const busy = chat.phase !== 'idle';
  const failedLoad = chat.loadError;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="help-ask-panel">
      <div
        ref={threadRef}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        aria-busy={busy}
        className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4"
        data-testid="help-chat-thread"
      >
        <ChatNotice />
        {chat.isLoadingConversation && (
          <div className="flex flex-col gap-3" data-testid="help-chat-loading" aria-busy="true" aria-label="Loading">
            <Skeleton className="h-10 w-2/3 self-end" />
            <Skeleton className="h-16 w-5/6" />
          </div>
        )}
        {failedLoad && (
          <HelpLoadError
            message="We couldn't open this conversation."
            onRetry={() => void chat.loadConversation(failedLoad.conversationId)}
            data-testid="help-chat-load-error"
          />
        )}
        {chat.messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={message}
            isLast={index === chat.messages.length - 1}
            onOpenArticle={inPanel ? openArticle : undefined}
            onRetry={() => void chat.retry()}
            onSearchHelp={searchHelp}
            onContactSupport={() => void contactSupport()}
            contactBusy={escalating}
            onRated={chat.applyRating}
          />
        ))}
        {chat.phase === 'waiting' && <TypingIndicator />}
        {chat.phase === 'streaming' && <StreamingMessage text={chat.streamingText} />}
      </div>
      <ChatComposer
        draft={chat.draft}
        onDraftChange={chat.setDraft}
        onSend={() => void chat.send()}
        busy={busy}
        rateLimit={chat.rateLimit}
        inputError={chat.inputError}
        onSearchHelp={searchHelp}
        onContactSupport={() => void contactSupport()}
        contactBusy={escalating}
      />
      {escalating && (
        <p role="status" className="sr-only" data-testid="help-escalating">
          Attaching your conversation to a support message
        </p>
      )}
    </div>
  );
}
